import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Archive, Loader2, Settings2, ClipboardCheck, AlertTriangle, CheckCircle2,
    Search as SearchIcon, UserPlus, Check,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { useGearsVisible } from "../lib/gearVisibility";

const COND_LABEL = { gebraucht: "Gebraucht", tageszulassung: "Tageszulassung", vorfuehr: "Vorführ", neu: "Neu" };

export default function PaperInventoryPage() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("inventory"); // inventory | research
    const [cfg, setCfg] = useState(null);
    const [overview, setOverview] = useState(null);
    const [items, setItems] = useState([]);
    const [research, setResearch] = useState([]);
    const [busy, setBusy] = useState(true);
    const [q, setQ] = useState("");

    const loadAll = useCallback(async () => {
        setBusy(true);
        try {
            const [c, o, inv, rs] = await Promise.all([
                api.get("/dealer/paper-storage/config"),
                api.get("/dealer/paper-storage/overview"),
                api.get("/dealer/paper-storage/inventory"),
                api.get("/dealer/paper-storage/research"),
            ]);
            setCfg(c.data); setOverview(o.data); setItems(inv.data.items || []); setResearch(rs.data.items || []);
        } catch (e) { console.error(e); }
        finally { setBusy(false); }
    }, []);

    useEffect(() => {
        if (loading) return;
        if (!user || !["b2b", "admin", "zweiradverkaeufer"].includes(user.role)) { navigate("/"); return; }
        loadAll();
    }, [user, loading, navigate, loadAll]);

    if (loading || !user) return <div className="p-12">Lädt…</div>;

    const filtered = items.filter((it) => {
        if (!q) return true;
        const s = q.toLowerCase();
        return [it.brand, it.model, it.vin, it.internal_number, it.slot].some((v) => (v || "").toLowerCase().includes(s));
    });

    return (
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="paper-inventory-page">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
                <div>
                    <h1 className="text-3xl font-black tracking-tight text-[#111827] flex items-center gap-2">
                        <Archive className="w-7 h-7 text-[#FFCC00]" /> Fahrzeugpapier-Inventur
                    </h1>
                    <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                        Zählung der Fahrzeugpapiere in <strong>Ordner-Reihenfolge</strong> (A → B → …). Bei Gebraucht-, Tageszulassungs- und Vorführfahrzeugen werden COC/ZB1/ZB2 abgefragt; fehlt etwas, landet das Fahrzeug auf der <strong>Nachrecherche-Liste</strong>.
                    </p>
                </div>
            </div>

            <ConfigCard cfg={cfg} onSaved={loadAll} />
            <OverviewBars overview={overview} />

            <div className="flex gap-2 mt-6 mb-4">
                <button onClick={() => setTab("inventory")} className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm border inline-flex items-center gap-1.5 ${tab === "inventory" ? "bg-[#111827] text-white border-[#111827]" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`} data-testid="paper-tab-inventory">
                    <ClipboardCheck className="w-3.5 h-3.5" /> Zählung ({items.length})
                </button>
                <button onClick={() => setTab("research")} className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm border inline-flex items-center gap-1.5 ${tab === "research" ? "bg-[#111827] text-white border-[#111827]" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`} data-testid="paper-tab-research">
                    <AlertTriangle className="w-3.5 h-3.5" /> Nachrecherche ({research.length})
                </button>
            </div>

            {busy ? (
                <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
            ) : tab === "inventory" ? (
                <>
                    <div className="mb-3 relative max-w-md">
                        <SearchIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche (Marke/Modell/FIN/Platz)" className="w-full border border-gray-300 rounded-sm pl-9 pr-3 py-2 text-sm" data-testid="paper-inventory-search" />
                    </div>
                    {filtered.length === 0 ? (
                        <div className="text-center text-gray-500 py-16 border border-dashed border-gray-300 rounded-sm" data-testid="paper-inventory-empty">Keine Fahrzeuge mit Papieren im Hause.</div>
                    ) : (
                        <div className="space-y-2" data-testid="paper-inventory-list">
                            {filtered.map((it) => <InventoryRow key={it.listing_id} item={it} onCounted={loadAll} />)}
                        </div>
                    )}
                </>
            ) : (
                research.length === 0 ? (
                    <div className="text-center text-gray-500 py-16 border border-dashed border-gray-300 rounded-sm" data-testid="paper-research-empty">Keine offenen Nachrecherchen.</div>
                ) : (
                    <div className="space-y-3" data-testid="paper-research-list">
                        {research.map((r) => <ResearchRow key={r.id} item={r} onChanged={loadAll} />)}
                    </div>
                )
            )}
        </div>
    );
}

function ConfigCard({ cfg, onSaved }) {
    const gearsOk = useGearsVisible();

    const [open, setOpen] = useState(false);
    const [folders, setFolders] = useState(26);
    const [sleeves, setSleeves] = useState(40);
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (cfg) { setFolders(cfg.folder_count); setSleeves(cfg.sleeves_per_folder); } }, [cfg]);
    const save = async () => {
        setSaving(true);
        try {
            await api.put("/dealer/paper-storage/config", { folder_count: Number(folders), sleeves_per_folder: Number(sleeves) });
            toast.success("Konfiguration gespeichert");
            setOpen(false); onSaved();
        } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
        finally { setSaving(false); }
    };
    if (!cfg || !gearsOk) return null;
    return (
        <div className="border border-gray-200 rounded-sm bg-white" data-testid="paper-config-card">
            <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-testid="paper-config-toggle">
                <span className="inline-flex items-center gap-2 font-bold text-sm text-[#111827]"><Settings2 className="w-4 h-4 text-[#FFCC00]" /> Ordner-Konfiguration: {cfg.folder_count} Ordner (A–{String.fromCharCode(64 + cfg.folder_count)}) · {cfg.sleeves_per_folder} Hüllen je Ordner</span>
                <span className="text-xs font-bold text-gray-400">{open ? "Schließen" : "Bearbeiten"}</span>
            </button>
            {open && (
                <div className="px-4 pb-4 border-t border-gray-100 pt-3 flex flex-wrap items-end gap-4">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 block mb-1">Anzahl Ordner (1–26, A–Z)</label>
                        <input type="number" min={1} max={26} value={folders} onChange={(e) => setFolders(e.target.value)} className="border border-gray-300 rounded-sm px-3 py-1.5 text-sm w-28" data-testid="paper-config-folders" />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 block mb-1">Hüllen je Ordner (1–200)</label>
                        <input type="number" min={1} max={200} value={sleeves} onChange={(e) => setSleeves(e.target.value)} className="border border-gray-300 rounded-sm px-3 py-1.5 text-sm w-28" data-testid="paper-config-sleeves" />
                    </div>
                    <button onClick={save} disabled={saving} className="bg-[#111827] text-white rounded-sm px-4 py-2 text-sm font-bold hover:bg-black disabled:opacity-60" data-testid="paper-config-save">{saving ? "Speichert…" : "Speichern"}</button>
                </div>
            )}
        </div>
    );
}

function OverviewBars({ overview }) {
    if (!overview) return null;
    const used = overview.occupied_count, total = overview.total;
    return (
        <div className="mt-3 border border-gray-200 rounded-sm bg-white p-4" data-testid="paper-overview">
            <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Belegung</span>
                <span className="text-xs text-gray-500">{used}/{total} belegt</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
                {overview.folders.map((f) => {
                    const pct = f.capacity ? Math.round((f.used / f.capacity) * 100) : 0;
                    const full = f.free === 0;
                    return (
                        <div key={f.folder} title={`Ordner ${f.folder}: ${f.used}/${f.capacity} belegt`} className={`text-[11px] font-bold rounded-sm px-2 py-1 border ${full ? "bg-red-50 border-red-200 text-red-700" : f.used > 0 ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-gray-50 border-gray-200 text-gray-500"}`} data-testid={`paper-overview-folder-${f.folder}`}>
                            {f.folder} · {f.used}/{f.capacity} <span className="opacity-60">({pct}%)</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function InventoryRow({ item, onCounted }) {
    const [open, setOpen] = useState(false);
    const [coc, setCoc] = useState(null);
    const [zb1, setZb1] = useState(null);
    const [zb2, setZb2] = useState(null);
    const [saving, setSaving] = useState(false);
    const needs = item.needs_check;

    const submit = async () => {
        if (needs && (coc === null || zb1 === null || zb2 === null)) { toast.error("Bitte COC, ZB1 und ZB2 beantworten"); return; }
        setSaving(true);
        try {
            const payload = needs ? { coc: !!coc, zb1: !!zb1, zb2: !!zb2 } : { coc: true, zb1: true, zb2: true };
            const r = await api.post(`/dealer/paper-storage/inventory/${item.listing_id}/count`, payload);
            toast.success(r.data.status === "ok" ? "Gezählt ✓ — alles vorhanden" : "Gezählt — auf Nachrecherche-Liste gesetzt");
            setOpen(false); onCounted();
        } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
        finally { setSaving(false); }
    };

    return (
        <div className="border border-gray-200 rounded-sm bg-white" data-testid={`paper-inventory-row-${item.listing_id}`}>
            <div className="flex items-center gap-3 px-3 py-2.5 flex-wrap">
                <span className="inline-flex items-center justify-center min-w-[44px] bg-[#111827] text-white font-bold text-sm rounded-sm px-2 py-1 font-mono-num" data-testid={`paper-slot-${item.listing_id}`}>{item.slot || "—"}</span>
                <div className="min-w-0 flex-1">
                    <div className="font-bold text-[#111827] truncate">{item.brand} {item.model}</div>
                    <div className="text-xs text-gray-500">FIN: <span className="font-mono-num">{item.vin || "—"}</span> · {COND_LABEL[item.condition] || item.condition || "—"}{item.internal_number ? ` · Nr. ${item.internal_number}` : ""}</div>
                </div>
                {item.has_open_research && <span className="text-[10px] font-bold uppercase bg-amber-100 text-amber-800 rounded-sm px-2 py-1">In Nachrecherche</span>}
                {item.papers_inventory_last_counted_at && <CheckCircle2 className="w-4 h-4 text-emerald-500" title="Bereits gezählt" />}
                <button onClick={() => setOpen((o) => !o)} className="text-xs font-bold border border-gray-300 rounded-sm px-3 py-1.5 hover:bg-gray-50" data-testid={`paper-count-btn-${item.listing_id}`}>Zählen</button>
            </div>
            {open && (
                <div className="px-3 pb-3 border-t border-gray-100 pt-3" data-testid={`paper-count-panel-${item.listing_id}`}>
                    {needs ? (
                        <div className="space-y-2">
                            <div className="text-xs font-bold text-gray-600">Prüffragen (Gebraucht/TZ/Vorführ)</div>
                            {[["COC / ABE", coc, setCoc, "coc"], ["ZB1 (Zulassung Teil I)", zb1, setZb1, "zb1"], ["ZB2 (Teil II / Brief)", zb2, setZb2, "zb2"]].map(([label, val, setter, key]) => (
                                <div key={key} className="flex items-center justify-between gap-2">
                                    <span className="text-sm text-gray-700">{label} vorhanden?</span>
                                    <div className="flex gap-1.5">
                                        <button onClick={() => setter(true)} className={`text-xs font-bold px-3 py-1 rounded-sm border ${val === true ? "bg-emerald-600 text-white border-emerald-600" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`} data-testid={`paper-${key}-yes-${item.listing_id}`}>Ja</button>
                                        <button onClick={() => setter(false)} className={`text-xs font-bold px-3 py-1 rounded-sm border ${val === false ? "bg-red-600 text-white border-red-600" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`} data-testid={`paper-${key}-no-${item.listing_id}`}>Nein</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-sm text-gray-500">Neufahrzeug — keine Prüffragen. Zählung bestätigt nur den Bestand.</div>
                    )}
                    <button onClick={submit} disabled={saving} className="mt-3 w-full bg-[#111827] text-white rounded-sm py-2 text-sm font-bold hover:bg-black disabled:opacity-60" data-testid={`paper-count-submit-${item.listing_id}`}>{saving ? "Speichert…" : "Zählung buchen"}</button>
                </div>
            )}
        </div>
    );
}

function ResearchRow({ item, onChanged }) {
    const [assignee, setAssignee] = useState("");
    const [busy, setBusy] = useState(false);
    const assign = async () => {
        if (!assignee) { toast.error("Bitte Mitarbeiter wählen"); return; }
        setBusy(true);
        try {
            await api.post(`/dealer/paper-storage/research/${item.id}/assign`, { assignee_id: assignee });
            toast.success("Aufgabe zugewiesen");
            onChanged();
        } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
        finally { setBusy(false); }
    };
    const resolve = async () => {
        setBusy(true);
        try { await api.post(`/dealer/paper-storage/research/${item.id}/resolve`); toast.success("Erledigt"); onChanged(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
        finally { setBusy(false); }
    };
    // eindeutige Vorschlagsliste (id)
    const seen = new Set();
    const sugg = (item.suggested_assignees || []).filter((s) => { if (seen.has(s.id)) return false; seen.add(s.id); return true; });
    return (
        <div className="border border-amber-200 bg-amber-50 rounded-sm p-3" data-testid={`paper-research-row-${item.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="font-bold text-[#111827]">{item.brand} {item.model} <span className="text-xs font-normal text-gray-500">· Platz {item.slot || "—"}</span></div>
                    <div className="text-xs text-gray-600">FIN: <span className="font-mono-num">{item.vin || "—"}</span>{item.internal_number ? ` · Nr. ${item.internal_number}` : ""}</div>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                        {(item.missing_labels || []).map((m) => <span key={m} className="text-[10px] font-bold uppercase bg-red-100 text-red-700 rounded-sm px-1.5 py-0.5">{m} fehlt</span>)}
                    </div>
                    {item.assignee_name && <div className="text-xs text-emerald-700 mt-1.5 inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Zugewiesen an {item.assignee_name}</div>}
                </div>
                <div className="flex items-center gap-2">
                    <select value={assignee} onChange={(e) => setAssignee(e.target.value)} className="border border-gray-300 rounded-sm px-2 py-1.5 text-xs bg-white max-w-[200px]" data-testid={`paper-research-assignee-${item.id}`}>
                        <option value="">Mitarbeiter wählen…</option>
                        {sugg.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.role_hint})</option>)}
                    </select>
                    <button onClick={assign} disabled={busy} className="text-xs font-bold bg-[#111827] text-white rounded-sm px-3 py-1.5 hover:bg-black inline-flex items-center gap-1 disabled:opacity-60" data-testid={`paper-research-assign-${item.id}`}><UserPlus className="w-3.5 h-3.5" /> Zuweisen</button>
                    <button onClick={resolve} disabled={busy} className="text-xs font-bold border border-gray-300 bg-white rounded-sm px-3 py-1.5 hover:bg-gray-50" data-testid={`paper-research-resolve-${item.id}`}>Erledigt</button>
                </div>
            </div>
        </div>
    );
}
